/**
 * Event Bus subscription composable following SOLID principles
 * Single Responsibility: Manages event bus lifecycle and subscriptions
 */
import { onMounted, onBeforeUnmount } from '@vue/composition-api';
import eventBus from "@/components/game/store/eventBus";

/**
 * Composable for managing event bus subscriptions with automatic cleanup
 * @param {string|Object} eventConfig - Event name or configuration object
 * @param {Function} handler - Event handler function (if eventConfig is string)
 * @returns {Object} Event management methods
 */
export function useEventBusSubscription(eventConfig, handler = null) {
  // Store active subscriptions for cleanup
  const activeSubscriptions = new Map();
  
  /**
   * Subscribe to a single event
   * @param {string} eventName - Name of the event
   * @param {Function} handlerFn - Handler function
   */
  const subscribe = (eventName, handlerFn) => {
    if (!eventName || typeof handlerFn !== 'function') {
      console.warn('Invalid event subscription parameters');
      return;
    }
    
    // Unsubscribe existing handler if exists
    if (activeSubscriptions.has(eventName)) {
      unsubscribe(eventName);
    }
    
    // Store the handler for cleanup
    activeSubscriptions.set(eventName, handlerFn);
    
    // Subscribe to event
    eventBus.$on(eventName, handlerFn);
  };
  
  /**
   * Unsubscribe from a specific event
   * @param {string} eventName - Name of the event
   */
  const unsubscribe = (eventName) => {
    const handler = activeSubscriptions.get(eventName);
    if (handler) {
      eventBus.$off(eventName, handler);
      activeSubscriptions.delete(eventName);
    }
  };
  
  /**
   * Unsubscribe from all events
   */
  const unsubscribeAll = () => {
    activeSubscriptions.forEach((handler, eventName) => {
      eventBus.$off(eventName, handler);
    });
    activeSubscriptions.clear();
  };
  
  /**
   * Emit an event
   * @param {string} eventName - Name of the event
   * @param {...any} args - Event arguments
   */
  const emit = (eventName, ...args) => {
    eventBus.$emit(eventName, ...args);
  };
  
  /**
   * Subscribe to multiple events at once
   * @param {Object} eventHandlers - Object mapping event names to handlers
   */
  const subscribeMultiple = (eventHandlers) => {
    Object.entries(eventHandlers).forEach(([eventName, handlerFn]) => {
      subscribe(eventName, handlerFn);
    });
  };
  
  // Handle different initialization patterns
  if (typeof eventConfig === 'string' && handler) {
    // Simple single event subscription
    onMounted(() => {
      subscribe(eventConfig, handler);
    });
  } else if (typeof eventConfig === 'object' && eventConfig !== null) {
    // Multiple event subscriptions
    onMounted(() => {
      subscribeMultiple(eventConfig);
    });
  }
  
  // Automatic cleanup on component unmount
  onBeforeUnmount(() => {
    unsubscribeAll();
  });
  
  return {
    subscribe,
    unsubscribe,
    unsubscribeAll,
    emit,
    subscribeMultiple,
    activeSubscriptions: activeSubscriptions // Read-only access
  };
}

/**
 * Specialized composable for the common 'received' event pattern
 * @param {Function} handler - Handler function for received events
 * @returns {Object} Received event management
 */
export function useReceivedEventSubscription(handler) {
  const { subscribe, unsubscribe, emit } = useEventBusSubscription();
  
  // Subscribe to 'received' event on mount
  onMounted(() => {
    subscribe('received', handler);
  });
  
  /**
   * Emit a received event
   * @param {string} itemType - Type of received item
   * @param {Object} item - Received item data
   */
  const emitReceived = (itemType, item) => {
    emit('received', itemType, item);
  };
  
  return {
    emitReceived,
    unsubscribe: () => unsubscribe('received')
  };
}

/**
 * Specialized composable for discovery-related event handling
 * @param {Function} updateDiscoveryHandler - Handler for discovery updates
 * @returns {Object} Discovery event management
 */
export function useDiscoveryEventSubscription(updateDiscoveryHandler) {
  const eventHandlers = {
    'received': updateDiscoveryHandler,
    'discovery:update': updateDiscoveryHandler,
    'item:found': updateDiscoveryHandler
  };
  
  const eventBus = useEventBusSubscription(eventHandlers);
  
  /**
   * Broadcast discovery update
   * @param {string} itemType - Type of discovered item
   * @param {Object} item - Discovered item
   */
  const broadcastDiscovery = (itemType, item) => {
    eventBus.emit('received', itemType, item);
    eventBus.emit('discovery:update', itemType, item);
    eventBus.emit('item:found', itemType, item);
  };
  
  return {
    ...eventBus,
    broadcastDiscovery
  };
}

